// Animated Modal component for all detail and confirmation popups
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import './Modal.css';

const Modal = ({ show, onClose, title, children, size = 'medium', drawer = false, footer = null, customClass = '' }) => {
  const modalRef = useRef(null);
  const prevShowRef = useRef(false);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Only focus modal container when opening, not on every update
      if (!prevShowRef.current && modalRef.current) {
        modalRef.current.focus();
      }
    }

    prevShowRef.current = show;

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [show, onClose]);

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={`modal-overlay${show ? ' show' : ''}${drawer ? ' drawer-overlay' : ''}${size === 'fullscreen' ? ' fullscreen' : ''}`}
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      ref={modalRef}
    >
      <div className={`modal ${size}${drawer ? ' drawer' : ''}${show ? ' drawer-open' : ''}${customClass ? ` ${customClass}` : ''}`}>
        {title && (
          <div className="modal-header">
            <h2 className="modal-title">{title}</h2>
            <button className="modal-close action-button" onClick={onClose} aria-label="Close modal">
              <FiX />
            </button>
          </div>
        )}
        <div className="modal-body">
          {children}
        </div>
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return ReactDOM.createPortal(modalContent, document.body);
  }

  return modalContent;
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  size: PropTypes.string,
  drawer: PropTypes.bool,
  footer: PropTypes.node,
  customClass: PropTypes.string
};

Modal.defaultProps = {
  title: '',
  children: null,
  size: 'medium',
  drawer: false,
  footer: null,
  customClass: ''
};

Modal.ConfirmationFooter = function({ onCancel, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel', confirmColor = 'btn-primary', cancelColor = 'btn-secondary' }) {
  return (
    <>
      <button className={`btn ${cancelColor}`} onClick={onCancel}>{cancelText}</button>
      <button className={`btn ${confirmColor}`} onClick={onConfirm}>{confirmText}</button>
    </>
  );
};

export default Modal;
export { Modal as ModalAnimated };